import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { HttpException } from '../../exceptions/index';
import { ApiService } from '../../services/index';
import {
  Stakeholder,
  StakeholderFilter,
  StakeholderOrder,
  StakeholderSortBy,
  StakeholderType,
} from '../../types/stakeholder.types';
import { useAppQuery } from '../use-app-query.hook';

type GetCompaniesStakeholderResponse = {
  stakeholders: Stakeholder[];
  totalPages: number;
  totalStakeholders: number;
};
export type FilterVariant = 'all' | StakeholderFilter;

const allStakeholderTypes = Object.values(StakeholderType);
export const useStakeholders = ({
  companyId,
  numberOfFetchedItems = 7,
  filter,
  orderBy,
  currentPage,
  search,
  searchType,
  sortBy,
  isTerminated,
  loadUsers = false,
}: {
  companyId: string;
  currentPage: number;
  filter: FilterVariant;
  isTerminated: boolean;
  loadUsers?: boolean;
  numberOfFetchedItems?: number;
  orderBy: null | StakeholderOrder;
  search: string;
  searchType: StakeholderType[];
  sortBy: null | StakeholderSortBy;
}) => {
  const queryClient = useQueryClient();
  const skip = (currentPage - 1 <= 0 ? 0 : currentPage - 1) * numberOfFetchedItems;
  const {
    data: { stakeholders, totalPages, totalStakeholders } = {},
    isLoading,
    refetch,
  } = useAppQuery<GetCompaniesStakeholderResponse, HttpException>({
    queryKey: [
      QueryKey.GET_STAKEHOLDERS,
      companyId,
      {
        filter,
        page: currentPage,
        sortBy,
        orderBy,
        skip,
        isTerminated,
        numberOfFetchedItems,
        searchType,
      },
    ],
    queryFn: () =>
      ApiService.get(
        { endpoint: BackendRoute.COMPANIES, routePath: [companyId, 'stakeholders'] },
        {
          queryParams: {
            take: numberOfFetchedItems,
            skip,
            search,
            searchType:
              searchType.length > 0 && searchType.length !== allStakeholderTypes.length
                ? searchType.join(',')
                : '',
            filter: filter === 'all' ? '' : filter,
            ...(orderBy && sortBy ? { sortBy, sortOrder: orderBy } : {}),
            isTerminated,
            loadUsers,
          },
        },
      ),
    enabled: !!companyId && searchType.length !== 0,
  });

  const invalidateQuery = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_STAKEHOLDERS] });
  }, [queryClient]);

  return {
    invalidateQuery,
    stakeholders: searchType.length === 0 ? [] : stakeholders || [],
    totalPages: searchType.length === 0 ? 0 : totalPages,
    totalStakeholders: totalStakeholders || 0,
    isLoading,
    refetch,
  };
};
